import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./AddRecordForm.css";
import InventoryServices from "../../services/InventoryServices";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import translations from "../../assets/locals/translations";

// Define form validation schema (with default English messages)
const validationSchema = Yup.object({
  model: Yup.string().required("Model is required"),
  location: Yup.string().required("Location is required"),
  bilNo: Yup.string().required("BIL NO is required"),
  date: Yup.date().required("Date is required"),
  bumCod: Yup.string().required("BUM COD is required"),
  chassis: Yup.string().required("Chassis is required"),
  engine: Yup.string().required("Engine is required"),
  ndp: Yup.string().required("NDP is required"),
  number22: Yup.string()
    .required("22 Number is required")
    .min(6, "22 Number must be at least 6 characters")
    .max(8, "22 Number must be at most 8 characters"),
  stockStatus: Yup.string().required("Status is required")

});

const AddRecordForm = () => {
  const location = useLocation();
  const editData = location.state?.data || null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const InventoryService = new InventoryServices();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];
  const [loading, setLoading] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [initialValues, setInitialValues] = useState({
    model: editData?.model || "",
    location: editData?.location || "",
    bilNo: editData?.bil_no || "",
    date: editData?.date
      ? new Date(editData.date).toISOString().split("T")[0]
      : "",
    bumCod: editData?.bum_cod || "",
    chassis: editData?.chassis || "",
    engine: editData?.engine || "",
    ndp: editData?.ndp || "",
    number22: editData?.number22 || "",
    stockStatus: editData?.stockStatus || "", 
  });

  const getModelists = async () => {
    setLoading(true);
    try {
      const response = await InventoryService.getModelists();
      if (response && response.isSuccess) {
        setModelOptions(
          response.result.map((val) => ({
            label: val.modalName,
            value: val.id,
          }))
        );
      }
    } catch (error) {
      console.error(error, "Error getting servicing list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getModelists();
  }, []);
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        try {
          if (!editData) {
            const response = await InventoryService.createStockLog(values);
            if (response && response.isSuccess) {
              dispatch(setShowMessage(response.message));
              navigate("/stock/billing");
            }
          } else {
            const response = await InventoryService.updateStockLog({
              ...values,
              id: editData.id,
            });
            if (response && response.isSuccess) {
              dispatch(setShowMessage(response.message));
              navigate("/stock/billing");
            }
          }
        } catch (error) {
          console.error(error, "Error creating stock");
        }
        setSubmitting(false);
        resetForm();
      }}
    >
      {({ isSubmitting }) => (
        <Form className="add-record-form">
          <div className="d-flex justify-content-between">
            <h4 className="text-secondary">{t.addbilling || "Model Record"}</h4>
            <button
              className="btn btn-outline-secondary d-flex align-items-center ms-1"
              onClick={() => {
                navigate("/stock/billing");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-arrow-left-circle me-2"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M8.354 11.354a.5.5 0 0 1-.708 0L4.5 8.207l3.146-3.147a.5.5 0 0 1 .708.708L5.707 8l2.647 2.646a.5.5 0 0 1 0 .708z" />
              </svg>
              {t.back || "Back"}
            </button>
          </div>
          {/* Use a grid layout for a responsive design */}
          <div className="form-grid">
            {/* BIL NO Field */}
            <div className="form-group">
              <label htmlFor="bilNo" className="form-label">
                {t.bilNo || "Bill No"}
              </label>
              <Field
                type="text"
                name="bilNo"
                id="bilNo"
                placeholder={t.bilNo || "Enter BIL NO"}
                className="form-input"
              />
              <ErrorMessage
                name="bilNo"
                component="div"
                className="error-message"
              />
            </div>

            {/* Date Field */}
            <div className="form-group">
              <label htmlFor="date" className="form-label">
                {(t.date || "Date").toUpperCase()}
              </label>
              <Field type="date" name="date" id="date" className="form-input" />
              <ErrorMessage
                name="date"
                component="div"
                className="error-message"
              />
            </div>

            {/* BUM COD Field */}
            <div className="form-group">
              <label htmlFor="bumCod" className="form-label">
                {t.bumCod || "BUM COD"}
              </label>
              <Field
                type="text"
                name="bumCod"
                id="bumCod"
                placeholder={t.bumCod || "Enter BUM COD"}
                className="form-input"
              />
              <ErrorMessage
                name="bumCod"
                component="div"
                className="error-message"
              />
            </div>

            {/* Model Dropdown as the first field */}
            <div className="form-group">
              <label htmlFor="model" className="form-label">
                {t.modal || t.model || "MODEL"}
              </label>
              <Field as="select" name="model" id="model" className="form-input">
                <option value="" label={t.selectmanufacturer || "Select Model"} />
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="model"
                component="div"
                className="error-message"
              />
            </div>

            {/* Chassis Field */}
            <div className="form-group">
              <label htmlFor="chassis" className="form-label">
                {t.chassis || "CHASSIS"}
              </label>
              <Field
                type="text"
                name="chassis"
                id="chassis"
                placeholder={t.chassisNumber || "Enter Chassis Number"}
                className="form-input"
              />
              <ErrorMessage
                name="chassis"
                component="div"
                className="error-message"
              />
            </div>

            {/* Engine Field */}
            <div className="form-group">
              <label htmlFor="engine" className="form-label">
                {t.engine || "ENGINE"}
              </label>
              <Field
                type="text"
                name="engine"
                id="engine"
                placeholder={t.engineNumber || "Enter Engine Number"}
                className="form-input"
              />
              <ErrorMessage
                name="engine"
                component="div"
                className="error-message"
              />
            </div>

            {/* NDP Field */}
            <div className="form-group">
              <label htmlFor="ndp" className="form-label">
                {t.ndp || "NDP"}
              </label>
              <Field
                type="text"
                name="ndp"
                id="ndp"
                placeholder={t.ndp || "Enter NDP"}
                className="form-input"
              />
              <ErrorMessage
                name="ndp"
                component="div"
                className="error-message"
              />
            </div>

            {/* 22 Number Field */}
            <div className="form-group">
              <label htmlFor="number22" className="form-label">
                22 NUMBER
              </label>

              <Field
                type="text"
                name="number22"
                id="number22"
                placeholder="Enter 22 Number"
                className="form-input"
              />

              <ErrorMessage
                name="number22"
                component="div"
                className="error-message"
              />
            </div>


            {/* Location Field */}
            <div className="form-group">
              <label htmlFor="location" className="form-label">
                {t.location || "LOCATION"}
              </label>
              <Field
                type="text"
                name="location"
                id="location"
                placeholder={t.location || "Enter Location"}
                className="form-input"
              />
              <ErrorMessage
                name="location"
                component="div"
                className="error-message"
              />
            </div>
          </div>

          {/* Stock Status Field */}
        <div className="form-group">
              <label htmlFor="model" className="form-label">
                 STOCK STATUS
              </label>
              <Field as="select" name="stockStatus" id="stockStatus" className="form-input">
                <option value="" label="Select Status" />
                <option value="IN_STOCK">In Stock</option>
                <option value="IN_DEPO">In Depot</option>
               
              </Field>
              <ErrorMessage
                name="stockStatus"
                component="div"
                className="error-message"
              />
            </div>

          <div className="form-actions d-flex justify-content-start">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-submit"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-plus-circle me-2"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
              </svg>
              {t.submit || "Submit"}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddRecordForm;
